<script module>
  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   */
</script>

<script>
  import MapComponent from "$lib/components/Map/Map.svelte";
  import DetailHeader from "$lib/components/DetailHeader.svelte";
  import Table from "$lib/components/Table/Table.svelte";
  import { Block, BasicDropdown, Theme } from "@urbaninstitute/dataviz-components";
  import windowWidth from "$lib/stores/windowWidth.js";
  import { createConfig, setConfig } from "$lib/utils/config.js";
  import { getStatesInfo, getStateName } from "$utils/states.js";
  import { formatTextTemplate } from "$utils/text.js";
  import { ToolData } from "$utils/data.js";
  import InfoBox from "./InfoBox.svelte";
  import InfoTable from "./InfoTable.svelte";
  import LineChart from "./Chart/LineChart.svelte";
  import {
    dollarFormat,
    percentFormat,
    normalFormat,
    precisePercentFormat
  } from "$utils/formats.js";

  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   * @property { boolean } [hideDropdown = false]
   */

  /** @type {MapToolProps} */
  const {
    data,
    tableData,
    configData,
    mapType = "geo",
    showStateSummary,
    accentColor = "#fdbf11",
    featureOption = "states",
    dropdownLabel = "Select a metric to view",
    tablePlaceholderLabel = "Select a state or district to see data by county",
    hideDropdown = false
  } = $props();

  // use this effect to load data from the URLs passed as properties
  $effect(() => {
    // fetch primary data and store in $state
    if (!parsedData) {
      parsedData = new ToolData(data, tableData, configData);
    }
    // fetch config data and store in $state
    if (!parsedConfigData) {
      /** @satisfies { import("$utils/config").ColumnConfig[]} */
      parsedConfigData = createConfig(configData, accentColor);
    }
  });

  // hard coded values for this instance
  const MAP_COLUMNS = ["pctchange_dollar_medhhincome"];
  const TOOLTIP_COLUMNS = ["pctchange_dollar_medhhincome"];

  /**
   * @param {Record<string, any>[]} allData
   * @param {string} map_id
   * @returns {Record<string, any>[]}
   */
  function getTableData(allData, map_id) {
    return allData.filter((d) => d.map_id === map_id);
  }

  let currentState = $state();
  let currentMetric = $state();
  // use effect to update current Metric to first data column if it isn't defined
  $effect(() => {
    if (parsedData && !currentMetric) {
      currentMetric = parsedData.getDataColumns()[0];
    }
  });

  /**
   * State to hold primary data
   * @type {ToolData | undefined}
   */
  let parsedData = $state();
  // state to hold configuration data
  let parsedConfigData = $state();
  // is the tool using the primary data for the table?
  let tableUsesMainData = $state(true);

  // set the config data into the context so child components can access it without prop drilling
  setConfig(() => parsedConfigData);

  // parse state summary into boolean
  const shouldShowStateSummary = $derived(showStateSummary === "true" && !tableUsesMainData);

  // derived state to hold the current table data, which is filtered based on the state selection
  let currentTableData = $derived(
    parsedData && currentState
      ? parsedData.getStateTableData(currentState, shouldShowStateSummary)
      : []
  );

  // derived state to hold the current map configuration, which is set based on the metric selection
  let currentMapConfig = $derived(
    currentMetric && parsedConfigData ? parsedConfigData.columns.get(currentMetric) : undefined
  );

  const stateDropdownData = getStatesInfo(featureOption).map(({ fips, state }) => ({
    value: fips,
    label: state
  }));

  const currentStateData = $derived(parsedData ? parsedData.getRow(currentState) : undefined);
  const unitedStatesData = $derived(parsedData ? parsedData.getRow("NA") : undefined);
  const currentStateVariables = $derived(getStateVariables(currentStateData));

  // Extract colNames from parsedData
  let tableColNames = $derived(parsedData ? parsedData.getTableColumns() : []);

  // build dropdown options from colNames
  const dataMetrics = $derived(
    parsedData && parsedConfigData
      ? parsedData
          .getDataColumns()
          .filter((k) => MAP_COLUMNS.includes(k)) // manual override
          .map((k) => ({ value: k, label: parsedConfigData.columns.get(k).column_label }))
      : []
  );

  // $inspect(parsedData);
  let incomeTableData = $derived(
    currentStateData
      ? [
          {
            "Income metric": "Median household income",
            "1970": dollarFormat.format(currentStateData.medhhincome1970),
            "2023": dollarFormat.format(currentStateData.medhhincome2023),
            Change: precisePercentFormat.format(currentStateData.pctchange_dollar_medhhincome)
          },
          {
            "Income metric": "State income rank",
            "1970": normalFormat.format(currentStateData.rank1970hh),
            "2023": normalFormat.format(currentStateData.rank2023hh),
            Change:
              (+currentStateData.change_rank_medhhincome > 0 ? "+" : "") +
              normalFormat.format(currentStateData.change_rank_medhhincome)
          }
        ]
      : []
  );
  let incomeTableCols = ["Income metric", "1970", "2023", "Change"];

  let additionalFactorsTableData = $derived(
    currentStateData
      ? [
          {
            "Associated socioeconomic factors": "Bachelor's degree holders",
            "Change (1970 to 2023)": percentFormat.format(currentStateData.change_b_aplus1970_23)
          },
          {
            "Associated socioeconomic factors": "Manufacturing workers",
            "Change (1970 to 2023)": percentFormat.format(
              currentStateData.changepct_manufacturing1970_23
            )
          },
          {
            "Associated socioeconomic factors": "Residents 19 and younger",
            "Change (1970 to 2023)": percentFormat.format(currentStateData.changeunder20_1970_23)
          },
          {
            "Associated socioeconomic factors": "Residents 64 and older",
            "Change (1970 to 2023)": percentFormat.format(currentStateData.changeover64_1970_23)
          },
          {
            "Associated socioeconomic factors": "Foreign-born population",
            "Change (1970 to 2023)": percentFormat.format(currentStateData.changeforeignborn1970_23)
          }
        ]
      : []
  );
  let additionalFactorsCols = ["Associated socioeconomic factors", "Change (1970 to 2023)"];

  let lineChartColsToPull = [
    "x1970medhhincome1970",
    "x1970medhhincome1980",
    "x1970medhhincome1990",
    "x1970medhhincome2000",
    "x1970medhhincome2010",
    "x1970medhhincome2020",
    "x1970medhhincome2023"
  ];
  let lineChartColsForChart = $derived(lineChartColsToPull.map((d) => +d.substring(d.length - 4)));
  let lineChartData = $derived(
    currentStateData && unitedStatesData
      ? lineChartColsForChart.map((d, i) => ({
          year: d,
          state: currentStateData[lineChartColsToPull[i]],
          nation: unitedStatesData[lineChartColsToPull[i]]
        }))
      : []
  );

  let lineChartAllStateData = $derived.by(() => {
    if (!parsedData) return [];
    let yearData = [
      { year: 1970 },
      { year: 1980 },
      { year: 1990 },
      { year: 2000 },
      { year: 2010 },
      { year: 2020 },
      { year: 2023 }
    ];
    for (let oneStateData of parsedData.data) {
      let stateName = oneStateData.map_id == "NA" ? "US" : getStateName(oneStateData.map_id);
      yearData[0][stateName] = oneStateData.x1970medhhincome1970;
      yearData[1][stateName] = oneStateData.x1970medhhincome1980;
      yearData[2][stateName] = oneStateData.x1970medhhincome1990;
      yearData[3][stateName] = oneStateData.x1970medhhincome2000;
      yearData[4][stateName] = oneStateData.x1970medhhincome2010;
      yearData[5][stateName] = oneStateData.x1970medhhincome2020;
      yearData[6][stateName] = oneStateData.x1970medhhincome2023;
    }

    return yearData;
  });
  // $inspect(lineChartAllStateData);

  /**
   * @param {CustomEvent<{props: {fips: string}}>} e
   */
  function handleStateSelect(e) {
    if (!e) {
      currentState = null;
      return;
    }
    if (e.detail) {
      const { fips, map_id } = e.detail.props;
      currentState = fips || map_id;
    } else {
      currentState = null;
    }
  }

  /**
   * @param {string} metric
   * @param {number} maxWidth
   */
  function getDropdownWidth(metric, maxWidth) {
    return Math.min(220, maxWidth);
  }
  function getStateVariables(stateData) {
    if (!parsedConfigData) {
      return undefined;
    }
    let result = {
      state: getStateName(stateData.map_id)
    };
    for (let key in stateData) {
      let colConfig = parsedConfigData.columns.get(key);
      if (colConfig) {
        result[key] = colConfig.format(stateData[key]);
      } else {
        result[key] = stateData[key];
      }
    }
    return result;
  }

  /**
   * @param {number} metric
   * @returns {string}
   */
  function addOrdinal(metric) {
    if (metric % 10 == 1 && metric != 11) return metric + "st";
    if (metric % 10 == 2 && metric != 12) return metric + "nd";
    if (metric % 10 == 3 && metric != 13) return metric + "rd";
    return metric + "th";
  }
</script>

<svelte:window bind:innerWidth={$windowWidth} />
<Theme>
  <Block width="body">
    <h4 class="tool-title">
      Percent Change in Household Income, 1970–2023 (inflation-adjusted dollars)
    </h4>
  </Block>
  <Block width="wide">
    {#if parsedData && parsedConfigData}
      {#if !hideDropdown}
        <div class="tool-header" style={`--accent-color: ${accentColor};`}>
          {dropdownLabel}
          <BasicDropdown
            data={dataMetrics}
            variant="secondary-yellow"
            inlineLabel="Select a metric to view"
            id="metric-select"
            placeholder={null}
            on:change={(e) => {
              // logClickToGA(e.target, "metric-select--" + e.target.value);
            }}
            bind:value={currentMetric}
          />
        </div>
      {/if}
      {#if currentMapConfig}
        <MapComponent
          data={parsedData.data}
          metric={currentMetric}
          onStateSelect={handleStateSelect}
          colorPalette={currentMapConfig.scale_color}
          scaleBreaks={currentMapConfig.scale_breaks}
          scaleType={currentMapConfig.scale_type}
          minMax={currentMapConfig.scale_min_max}
          {featureOption}
          {mapType}
          bind:currentState
          tooltipColumns={TOOLTIP_COLUMNS}
        />
      {/if}

      <DetailHeader bind:currentState {stateDropdownData} label={tablePlaceholderLabel} />

      <!-- <Table data={currentTableData} cols={tableColNames} labelColumn={!tableUsesMainData} /> -->

      <div class="infobox-container">
        <div class="first-column">
          <InfoBox>
            <h4>
              How has median household income changed{currentState ? " in " : ""}<span
                class="state-name">{currentState ? getStateName(currentState) : ""}</span
              >?
            </h4>
            {#if currentState}
              <p>
                In <span class="state-name">{getStateName(currentState)}</span>, median household
                income has
                {currentStateData.pctchange_dollar_medhhincome > 0 ? "grown" : "fallen"} by {precisePercentFormat
                  .format(currentStateData.pctchange_dollar_medhhincome)
                  .replace("%", " percent")}
                compared with the national growth rate of 32 percent. As a result,
                <span class="state-name">{getStateName(currentState)}</span>
                now has the
                {currentStateData.rank2023hh == 1
                  ? " highest"
                  : currentStateData.rank2023hh == 50
                    ? " lowest"
                    : addOrdinal(currentStateData.rank2023hh) + "-highest"} median income out of all
                50 states.
              </p>
              <InfoTable data={incomeTableData} cols={incomeTableCols} />
              {#key currentState}
                <p class="chart-header">
                  <i>Percent change in median household income from 1970 to 2023</i>
                </p>
                <LineChart data={lineChartAllStateData} {currentState} />
              {/key}
            {/if}
          </InfoBox>
        </div>
        <div class="second-column">
          <InfoBox>
            <h4>
              What socioeconomic factors have driven {currentState ? "" : " a state"}<span
                class="state-name">{currentState ? getStateName(currentState) : ""}</span
              >’s change in median household income?
            </h4>
            {#if currentState}
              <p>
                Nationally, our analysis finds that higher educational attainment and an increase in
                immigrant residents are associated with higher median income growth. Changes in
                manufacturing jobs have had disparate effects by region, and population growth
                showed positive but nonsignificant correlation with income growth. Below, see how
                these socioeconomic factors changed in <span class="state-name"
                  >{currentState ? getStateName(currentState) : ""}</span
                > from 1970 to 2023.
              </p>
              <InfoTable data={additionalFactorsTableData} cols={additionalFactorsCols} />
            {/if}
          </InfoBox>
        </div>
      </div>
    {/if}
  </Block>
</Theme>

<style>
  h4.tool-title {
    margin-bottom: var(--spacing-12) !important;
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    margin-bottom: var(--spacing-8);
    text-align: center;
  }
  .tool-header :global(.dropdown-parent) {
    display: inline-block;
  }
  .tool-header :global(.dropdown-container .icons svg) {
    display: block;
  }
  .tool-header :global(.dropdown-container select.dropdown-select) {
    background-color: var(--accent-color);
    border: var(--accent-color);
  }
  .tool-header :global(select) {
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header :global(select option) {
    font-size: var(--font-size-normal) !important;
  }
  .state-name {
    border-bottom: solid var(--color-yellow) 4px;
    /* text-underline-offset: 4px; */
  }
  p.chart-header {
    margin-top: 30px;
  }
  .infobox-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-2);
    @media screen and (max-width: 1020px) {
      grid-template-columns: 1fr;
    }
    h4 {
      margin-top: 0;
    }
  }
  @media (max-width: 540px) {
    .tool-header :global(select) {
      font-size: var(--font-size-xl) !important;
    }
    .tool-header {
      font-size: var(--font-size-xl) !important;
    }
  }
</style>
